import { Injectable } from '@angular/core';
import { CoreService } from 'src/app/common/http-services/core.service';
import { CancelSubscription } from 'src/app/common/models/billing/cancel-subscription';
import { ChangePaymentMethod } from 'src/app/common/models/billing/change-payment-method';
import { CreatePaymentIntent } from 'src/app/common/models/billing/create-payment-intent';
import { CreateSubscription } from 'src/app/common/models/billing/create-subscription';
import { BulkChangeSubscriptionRequest } from 'src/app/common/models/billing/bulk-change-subscription';
import { UpdateSubscription } from 'src/app/common/models/billing/update-subscription';
import { environment } from 'src/environments/environment';
import { BillingProduct } from './models/billing-product';
import { MessageType, UtilsService } from 'src/app/common';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BillingService {

  constructor(private core: CoreService<{}>,private utils: UtilsService) { }

  public async getSubscription(subscriptionId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/subscription/${subscriptionId}`));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async getUserSetupIntent(billingId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/subscription/setupIntent/billingId/${billingId}`));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async deleteSubscriptionItem(subscriptionItemId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.delete(`${environment.billingServiceUrl}/subscription/subscriptionItem/${subscriptionItemId}`));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async getProductWithPrices(userId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/billing/productWithPrices/${userId}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getProductWithPricesByOrganization(organizationId: number, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/billing/productWithPrices/organization/${organizationId}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getPriceWithProduct(priceId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/billing/priceWithProduct/${priceId}`));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async createSubscription(createSubscription: CreateSubscription, trial: boolean = false, handleError: boolean = false) {
    const response = {data: null, error: null};

    const url = trial ? `${environment.billingServiceUrl}/subscription/trial` : `${environment.billingServiceUrl}/subscription`;

    try {
      response.data = await firstValueFrom(this.core.post(url, createSubscription));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  // New: create subscription with multiple priceIds
  public async createSubscriptionMultiple(payload: { priceIds: string[], billingId: string }, trial: boolean = false, handleError: boolean = false) {
    const response = { data: null, error: null };
    const url = trial ? `${environment.billingServiceUrl}/subscription/multiple/trial` : `${environment.billingServiceUrl}/subscription/multiple`;
    try {
      response.data = await firstValueFrom(this.core.post(url, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }
    return response;
  }

  // New: get multiple prices with product info for order summary
  public async getPricesWithProducts(priceIds: string[], handleError: boolean = false) {
    const response = { data: null, error: null };
    try {
      response.data = await firstValueFrom(this.core.post(`${environment.billingServiceUrl}/billing/pricesWithProducts`, { priceIds }));
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }
    return response;
  }

  public async createPaymentIntent(createPaymentIntent: CreatePaymentIntent, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.billingServiceUrl}/payment/paymentIntent`, createPaymentIntent));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async updateSubscription(updateSubscription: UpdateSubscription, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.bulkPut(`${environment.billingServiceUrl}/subscription/${updateSubscription.subscriptionId}`, updateSubscription));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async cancelSubscriptionAtPeriodEnd(cancelSubscription: CancelSubscription, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.billingServiceUrl}/subscription/cancelAtPeriodEnd`, cancelSubscription));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async cancelSubscription(cancelSubscription: CancelSubscription, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.delete(`${environment.billingServiceUrl}/subscription/${cancelSubscription.subscriptionId}`));
    } catch (error) {
        response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getUserBillingProducts(billingId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.identityUrl}/billingProduct/billingUser/${billingId}`));
    } catch (error) {
        response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async updateBillingProduct(billProdId: number, payload: BillingProduct, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.put(`${environment.identityUrl}/billingProduct`, payload));
    } catch (error) {
        response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async changePaymentMethod(changePaymentMethod: ChangePaymentMethod, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.bulkPut(`${environment.billingServiceUrl}/subscription/changePaymentMethod`, changePaymentMethod));
    } catch (error) {
        response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getUserInfo(billingId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.identityUrl}/user/billing/${billingId}`));

      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      currentUser.user = response.data;
      localStorage.setItem('currentUser', JSON.stringify(currentUser));

    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async getUserByUserId(userId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.identityUrl}/user/${userId}`));

      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      currentUser.user = response.data;
      localStorage.setItem('currentUser', JSON.stringify(currentUser));

    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async getCardInfo(paymentMethodId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/billing/cardInfo/${paymentMethodId}`));

    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }
    return response;
  }

  public async getUserInvoices(billingId: string, handleError: boolean = false) {
    const response = {data: null, error: null};

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/invoice/allByCustomer/${billingId}`));
    } catch (error) {
        response.error = error;
        await this.handleError(error,handleError);
    }

    return response;
  }

  // New: bulk change subscription items (add, update, delete) in one request
  public async bulkChangeSubscription(payload: BulkChangeSubscriptionRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    try {
      response.data = await firstValueFrom(this.core.post(`${environment.billingServiceUrl}/subscription/bulkChange`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }
    return response;
  }

  private handleError(error: any, handleError : boolean = false) {
    this.utils.logger.error(error);
    if (typeof error.error === 'object' && (error.error.code === 1010 || error.error.code === 1007 || error.error.code === 1008 || error.error.code === 1009)) {
      this.utils.errorMessage(MessageType.ALERT, error.error.title);
    } else {
      if (handleError) {
        this.utils.errorMessage(MessageType.ALERT, 'There was an error processing your request. Please try again later.');
      }
    }
  }
}
